/**
 * RIS Viewer Context
 *
 * Global state management for the in-app RIS document viewer.
 * Provides methods to open/close the viewer and manages document state.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { buildApiUrl } from '../config/api';

// ===== TYPES =====

export interface Highlight {
  farbe: 'gelb' | 'orange';
  anfang: string;
  ende: string;
  grund?: string;
}

export interface HighlightResult {
  highlights: Highlight[];
  processingTimeMs?: number;
  fromCache?: boolean;
}

export interface DocumentMetadata {
  title: string;
  gericht: string;
  geschaeftszahl: string;
  datum: string;
  dokumentTyp?: 'RS' | 'TE' | 'KV' | 'PDF';
  ecli?: string;
  sourceUrl?: string | null;
  gesetzesnummer?: string;
  dokumentnummer?: string;
  imRisSeit?: string;
  eli?: string;
  entscheidungsart?: string;
  rechtsgebiet?: string;
  rechtssatznummer?: string;
}

export interface ViewerData {
  html: string;
  originalHtml?: string;
  metadata: DocumentMetadata;
  highlights: HighlightResult;
  insertedHighlightCount?: number; // Actual number of visible highlights
  fromCache: boolean;
  pdfDownloadUrl?: string | null;
}

export interface RISViewerState {
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  url: string | null;
  query: string;
  context: string;
  data: ViewerData | null;
}

export interface RISViewerContextType {
  state: RISViewerState;
  openViewer: (url: string, query: string, context?: string) => Promise<void>;
  openDocumentViewer: (documentId: string, query: string, context?: string) => Promise<void>;
  closeViewer: () => void;
  refreshViewer: () => Promise<void>;
}

// ===== CONTEXT =====

const RISViewerContext = createContext<RISViewerContextType | undefined>(undefined);

// ===== PROVIDER =====

interface RISViewerProviderProps {
  children: ReactNode;
}

export const RISViewerProvider: React.FC<RISViewerProviderProps> = ({ children }) => {
  const [state, setState] = useState<RISViewerState>({
    isOpen: false,
    isLoading: false,
    error: null,
    url: null,
    query: '',
    context: '',
    data: null
  });

  /**
   * Fetch document from the RIS Viewer API
   */
  const fetchDocument = async (url: string, query: string, context?: string): Promise<ViewerData> => {
    const params = new URLSearchParams({
      url,
      query,
      ...(context && { context })
    });

    // Cookies are sent automatically with credentials: 'include'
    const response = await fetch(
      buildApiUrl(`/api/ris/viewer?${params.toString()}`),
      {
        credentials: 'include'
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Unknown error');
    }

    return result.data;
  };

  const fetchDocumentFromRAG = async (documentId: string, query: string, context?: string): Promise<ViewerData> => {
    const params = new URLSearchParams({
      documentId,
      query,
      ...(context && { context })
    });

    const response = await fetch(
      buildApiUrl(`/api/ris/document-viewer?${params.toString()}`),
      { credentials: 'include' }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Unknown error');
    }

    return result.data;
  };

  /**
   * Open the viewer with a RIS URL
   */
  const openViewer = useCallback(async (url: string, query: string, context?: string) => {
    console.log(`[RISViewerContext] Opening viewer for: ${url.substring(0, 60)}...`);

    // Set loading state and open modal
    setState(prev => ({
      ...prev,
      isOpen: true,
      isLoading: true,
      error: null,
      url,
      query,
      context: context || '',
      data: null
    }));

    try {
      const data = await fetchDocument(url, query, context);

      setState(prev => ({
        ...prev,
        isLoading: false,
        data
      }));

      console.log(`[RISViewerContext] Document loaded: ${data.highlights.highlights.length} highlights`);
    } catch (error) {
      console.error('[RISViewerContext] Error loading document:', error);

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load document'
      }));
    }
  }, []);

  const openDocumentViewer = useCallback(async (documentId: string, query: string, context?: string) => {
    console.log(`[RISViewerContext] Opening document viewer for: ${documentId}`);

    setState(prev => ({
      ...prev,
      isOpen: true,
      isLoading: true,
      error: null,
      url: `document://${documentId}`,
      query,
      context: context || '',
      data: null
    }));

    try {
      const data = await fetchDocumentFromRAG(documentId, query, context);

      setState(prev => ({
        ...prev,
        isLoading: false,
        data
      }));

      console.log(`[RISViewerContext] Document loaded: ${data.highlights.highlights.length} highlights`);
    } catch (error) {
      console.error('[RISViewerContext] Error loading document:', error);

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load document'
      }));
    }
  }, []);

  /**
   * Close the viewer
   */
  const closeViewer = useCallback(() => {
    console.log('[RISViewerContext] Closing viewer');

    setState(prev => ({
      ...prev,
      isOpen: false,
      error: null
    }));
  }, []);

  /**
   * Refresh the current document (useful to regenerate highlights)
   */
  const refreshViewer = useCallback(async () => {
    if (!state.url || !state.query) {
      console.warn('[RISViewerContext] Cannot refresh: no URL or query');
      return;
    }

    console.log('[RISViewerContext] Refreshing viewer');

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      const data = await fetchDocument(state.url, state.query, state.context);

      setState(prev => ({
        ...prev,
        isLoading: false,
        data
      }));
    } catch (error) {
      console.error('[RISViewerContext] Error refreshing document:', error);

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to refresh document'
      }));
    }
  }, [state.url, state.query, state.context]);

  const value: RISViewerContextType = {
    state,
    openViewer,
    openDocumentViewer,
    closeViewer,
    refreshViewer
  };

  return (
    <RISViewerContext.Provider value={value}>
      {children}
    </RISViewerContext.Provider>
  );
};

// ===== HOOK =====

export const useRISViewer = (): RISViewerContextType => {
  const context = useContext(RISViewerContext);

  if (context === undefined) {
    throw new Error('useRISViewer must be used within a RISViewerProvider');
  }

  return context;
};

export default RISViewerContext;
